import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin } from "lucide-react";

const Footer = () => {
  return (
    <footer className="bg-[#1A1A1A] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-6">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <span className="font-display text-2xl text-white">Magnus Kor</span>
              <span className="w-px h-5 bg-[#B8860B]"></span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Talent Partners</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs mb-6">
              Talent advisory for VC-backed SaaS startups. From your first IC hire to your leadership bench.
            </p>
            <div className="space-y-1 mb-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <MapPin size={14} />
                <span>London, UK</span>
              </div>
            </div>
            <div className="flex gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#B8860B] transition-colors">
                <Linkedin size={16} />
              </a>
              <a href="mailto:office@magnuskor.com" className="w-9 h-9 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#B8860B] transition-colors">
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* For Startups */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#B8860B] mb-4 font-medium">For Startups</h4>
            <nav className="space-y-2.5">
              <Link to="/for-startups" className="block text-sm text-gray-400 hover:text-white transition-colors">Our Services</Link>
              <Link to="/for-startups" className="block text-sm text-gray-400 hover:text-white transition-colors">How We Work</Link>
              <Link to="/for-startups#contact-form" className="block text-sm text-gray-400 hover:text-white transition-colors">Start a Search</Link>
              <Link to="/contact" className="block text-sm text-gray-400 hover:text-white transition-colors">Book Consultation</Link>
            </nav>
          </div>

          {/* For Talent */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#B8860B] mb-4 font-medium">For Talent</h4>
            <nav className="space-y-2.5">
              <Link to="/jobs" className="block text-sm text-gray-400 hover:text-white transition-colors">Browse Jobs</Link>
              <Link to="/for-talent" className="block text-sm text-gray-400 hover:text-white transition-colors">Join Network</Link>
              <Link to="/insights" className="block text-sm text-gray-400 hover:text-white transition-colors">Career Insights</Link>
              <Link to="/for-talent#join-network" className="block text-sm text-gray-400 hover:text-white transition-colors">Submit Resume</Link>
            </nav>
          </div>

          {/* Jobs by Function */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#B8860B] mb-4 font-medium">Jobs by Function</h4>
            <nav className="space-y-2.5">
              <Link to="/jobs?department=Engineering" className="block text-sm text-gray-400 hover:text-white transition-colors">Engineering</Link>
              <Link to="/jobs?department=Product" className="block text-sm text-gray-400 hover:text-white transition-colors">Product</Link>
              <Link to="/jobs?department=Sales" className="block text-sm text-gray-400 hover:text-white transition-colors">Sales</Link>
              <Link to="/jobs?department=Growth" className="block text-sm text-gray-400 hover:text-white transition-colors">Growth & Marketing</Link>
              <Link to="/jobs?department=Customer Success" className="block text-sm text-gray-400 hover:text-white transition-colors">Customer Success</Link>
            </nav>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#B8860B] mb-4 font-medium">Company</h4>
            <nav className="space-y-2.5">
              <Link to="/about" className="block text-sm text-gray-400 hover:text-white transition-colors">About Us</Link>
              <Link to="/tools" className="block text-sm text-gray-400 hover:text-white transition-colors">Free Tools</Link>
              <Link to="/insights" className="block text-sm text-gray-400 hover:text-white transition-colors">Blog</Link>
              <Link to="/contact" className="block text-sm text-gray-400 hover:text-white transition-colors">Contact</Link>
            </nav>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-8 md:px-16 py-6">
          {/* Legal Info */}
          <div className="text-center md:text-left mb-4">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} Magnus Kor Talent Partners. All rights reserved.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              A division of <span className="text-gray-400">L&D Nexus Limited (UK)</span>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              UK Company No. 16450617 | <a href="mailto:office@magnuskor.com" className="text-[#B8860B] hover:text-[#D4A84B] transition-colors">office@magnuskor.com</a>
            </p>
          </div>
          
          {/* Links */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-800">
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/jobs?seniority=IC" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">IC Jobs</Link>
              <Link to="/jobs?seniority=Manager" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">Manager Jobs</Link>
              <Link to="/jobs?seniority=VP" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">VP Jobs</Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/privacy" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">Terms of Service</Link>
              <Link to="/cookies" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">Cookie Policy</Link>
              <a href="/api/sitemap.xml" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
